<script lang="ts">
    import { onMount } from 'svelte';
    import type { Event } from '../../api/events';
    import { getEvents, getEventUserUrlToken, resolveEventId } from '../../api/events';
    import { getUser } from '../../api/users';
    import { associationIdName, vvSettings } from '../../api/common';
    import * as t from '../../translations/Events.ts';
    import EventCard from '../../lib/Events/EventCard.svelte';
    import EventSpotlightGrid from '../../lib/Events/EventSpotlightGrid.svelte';
    import EventCalendarTimeline from '../../lib/Events/EventCalendarTimeline.svelte';
    import SkeletonLoader from '../../lib/Events/SkeletonLoader.svelte';
    import EmptyState from '../../lib/Events/EmptyState.svelte';

    let events: Event[] = [];
    let spotlightEvents: Event[] = [];
    let loading = true;
    let error = '';
    let activeTab: 'all' | 'registered' = 'all';
    
    // Restore tab from session storage or URL
    onMount(async () => {
        const urlParams = new URLSearchParams(window.location.hash.split('?')[1] || '');
        const tabParam = urlParams.get('tab');
        if (tabParam === 'registered') {
            activeTab = 'registered';
        } else {
            const savedTab = sessionStorage.getItem('eventsActiveTab');
            if (savedTab === 'registered') {
                activeTab = 'registered';
            }
        }
        
        await loadEvents();
    });

    /**
     * Mirrors Petitions' resolvePetitionUserToken: prefer the server's source
     * of truth (/Api/Users/GetRemembered) so we don't carry a stale vvSettings
     * token after cross-page user switches, and fall back to the server-
     * injected vvSettings token only if that lookup fails (offline-ish dev
     * builds, very early page load, etc.).
     *
     * On localhost without a real remember-me cookie this returns undefined,
     * which causes getEvents() to make an anonymous call -- isRegistered will
     * always be false there. In deployed environments either the cookie OR
     * the server-injected token will resolve, so cards render with the
     * correct registered state without any further plumbing.
     */
    async function resolveEventUserToken(): Promise<string | undefined> {
        try {
            const rememberedUser = await getUser();
            const token = rememberedUser?.userToken;
            if (typeof token === 'string' && token.trim().length > 0) return token.trim();
            return undefined;
        } catch {
            const tokenFromSettings = getEventUserUrlToken();
            return tokenFromSettings || undefined;
        }
    }

    async function loadEvents() {
        loading = true;
        error = '';

        try {
            // Resolve the current user's URL token (if any) so the list call can
            // stamp isRegistered=true on cards the user has registered for. The
            // REST EventConverter.ConvertSearchResponse(user) does this in a
            // single batch DB query per request -- no N+1 problem. When no token
            // is available (anonymous visit / localhost without real cookie)
            // the call falls through to anonymous and every isRegistered is
            // false, which is the correct behaviour.
            const userToken = await resolveEventUserToken();
            const response = await getEvents(userToken);
            spotlightEvents = response.spotlightEvents || [];
            events = response.otherEvents || [];

            if (tryRedirectSingleEventRegister([...spotlightEvents, ...events])) {
                return;
            }
        } catch (err) {
            console.error('Failed to fetch events:', err);
            error = t.Loading_Error();
        } finally {
            loading = false;
        }
    }
    
    function getSiteSegment(): string {
        const rawSiteNumber = (vvSettings as any)?.siteNumber;
        return rawSiteNumber ? `/${rawSiteNumber}` : '';
    }

    /** Full-page navigation to the canonical MVC route so server-side tracking tags render. */
    function buildRegisterUrl(eventId: number, fromSingle = false): string {
        const q = fromSingle ? '?fromSingle=1' : '';
        return `/${associationIdName}${getSiteSegment()}/Events/${eventId}/Register${q}`;
    }

    function handleRegister(event: Event) {
        const id = resolveEventId(event);
        if (!id) return;
        window.location.href = buildRegisterUrl(id);
    }

    /** Auto-redirect to the canonical server route so tracking tags render; skip if already on Register. */
    function tryRedirectSingleEventRegister(list: Event[]): boolean {
        if (list.length !== 1) return false;
        const id = resolveEventId(list[0]);
        if (!id) return false;
        const target = buildRegisterUrl(id, true);
        if (window.location.pathname.toLowerCase().endsWith(`/events/${id}/register`)) {
            return true;
        }
        window.location.replace(target);
        return true;
    }
    
    function switchTab(tab: 'all' | 'registered') {
        activeTab = tab;
        sessionStorage.setItem('eventsActiveTab', tab);
    }
    
    // Computed
    $: allEvents = [...spotlightEvents, ...events];
    $: registeredEvents = allEvents.filter(e => e.isRegistered);
</script>

<div class="@container event-list-page w-full m-auto max-w-[1000px] py-4 px-4 md:px-8 flex flex-col gap-8 interfont">
    <!-- Page Header -->
    <div class="flex flex-col gap-2">
        <h1 class="text-3xl md:text-4xl mt-1">{t.Events_Header()}</h1>
    </div>

    <!-- Featured events (Petitions / legacy Events: hero under title, above filters) -->
    {#if !loading && activeTab === 'all' && spotlightEvents.length > 0}
        <div class="w-full">
            <EventSpotlightGrid events={spotlightEvents} onRegister={handleRegister} />
        </div>
    {/if}

    <!-- Tabs -->
    <div class="flex items-center gap-4 border-b border-gray-200 mt-2" data-testid="tabs">
        <button
            type="button"
            class="pb-2 text-base text-gray-500 cursor-pointer transition-colors {activeTab === 'all' 
                ? 'font-medium !text-black !border-b-2 !border-black' 
                : 'hover:text-gray-700'}"
            on:click={() => switchTab('all')}
        >
            {t.All_Events_Tab()}
        </button>
        
        <div class="h-10 w-px bg-gray-200"></div>
        
        <button
            type="button"
            class="pb-2 text-base text-gray-500 cursor-pointer transition-colors flex items-center gap-2 {activeTab === 'registered' 
                ? 'font-medium !text-black !border-b-2 !border-black' 
                : 'hover:text-gray-700'}"
            on:click={() => switchTab('registered')}
        >
            {t.My_Registrations_Tab()}
            {#if registeredEvents.length > 0}
                <span class="inline-flex items-center justify-center px-2 py-0.5 text-xs font-medium rounded-full {activeTab === 'registered' ? 'bg-black text-white' : 'bg-gray-100 text-gray-600'}">
                    {registeredEvents.length}
                </span>
            {/if}
        </button>
    </div>

    <!-- Content Area -->
    <div class="mt-2">
        {#if loading}
            <SkeletonLoader count={3} />
        {:else if error}
            <p class="text-red-500 text-center py-8">{error}</p>
        {:else if activeTab === 'all'}
            <!-- All Events View -->
            {#if allEvents.length === 0}
                <EmptyState
                    title={t.No_Events_Title()}
                    description={t.No_Events_Description()}
                    testId="events-empty-state"
                />
            {:else}
                <!-- All events in list too (Petitions: spotlight + others in cards below hero) -->
                <div class="w-full flex flex-col gap-6">
                    {#each allEvents as event (event.id)}
                        <div>
                            <EventCard {event} onRegister={handleRegister} />
                        </div>
                    {/each}
                </div>
            {/if}
        {:else}
            <!-- My Registrations View -->
            {#if registeredEvents.length === 0}
                <EmptyState
                    title={t.My_Registrations_Tab()}
                    description={t.No_Registrations_Message()}
                    testId="events-registrations-empty-state"
                />
            {:else}
                <!-- Calendar Timeline -->
                <EventCalendarTimeline events={registeredEvents} />
                
                <!-- Event Cards -->
                <div class="w-full flex flex-col gap-6">
                    {#each registeredEvents as event (event.id)}
                        <div>
                            <EventCard {event} onRegister={handleRegister} />
                        </div>
                    {/each}
                </div>
            {/if}
        {/if}
    </div>
</div>

<style>
    :global(.event-list-page h1) {
        font-size: 2rem;
        line-height: 2.5rem;
        color: #111827;
        font-weight: 700;
    }

    /* Host site VoterVoice.css: .vvBody ul { padding-left: 2em } — keep any future lists flush */
    :global(.event-list-page ul) {
        padding-left: 0;
        margin-left: 0;
    }
</style>
